(function (root) {
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const cartKey = cart => JSON.stringify([cart.currency, cart.total_price, cart.total_weight, cart.items.map(item => [item.key, item.id, item.quantity, item.final_line_price])]);
  const rateCity = name => normalize(String(name || '').replace(/\s+(?:hasta\s+\d+(?:[.,]\d+)?|\d+(?:[.,]\d+)?\s*[-–]\s*\d+(?:[.,]\d+)?)\s*kg\s*$/i, ''));
  function nationalOptions(rates, selection, currency, selector) {
    return (rates || []).filter(rate => rateCity(rate.name) === normalize(selection.city)
      && (!rate.currency || rate.currency === currency)
      && /^\d+(?:\.\d{1,2})?$/.test(String(rate.price))).map(rate => ({
      id: JSON.stringify([rate.code || rate.name, rate.source, rate.price]),
      label: rate.presentment_name || rate.name,
      price: Math.round(Number(rate.price) * 100),
      time: selector?.nationalPromise?.(rate.description)?.promise || rate.description || '',
    }));
  }
  async function quoteNational(selection, currency, selector, fetcher, prefix, signal, wait = ms => new Promise(resolve => setTimeout(resolve, ms))) {
    if (selection?.country !== 'Colombia' || !selection.city || !selection.department) throw new Error('destination');
    const params = new URLSearchParams({ 'shipping_address[country]': 'Colombia', 'shipping_address[province]': selection.department, 'shipping_address[city]': selection.city });
    const config = { credentials: 'same-origin', cache: 'no-store', signal };
    const start = await fetcher(`${prefix}cart/prepare_shipping_rates.json?${params}`, { ...config, method: 'POST' });
    if (!start.ok) throw new Error('shipping');
    for (let attempt = 0; attempt < 10; attempt += 1) {
      if (signal.aborted) throw new Error('cancelled');
      const response = await fetcher(`${prefix}cart/async_shipping_rates.json?${params}`, config);
      if (!response.ok) throw new Error('shipping');
      const data = await response.json();
      if (data !== null) {
        if (!Array.isArray(data.shipping_rates)) throw new Error('shipping');
        return nationalOptions(data.shipping_rates, selection, currency, selector);
      }
      await wait(1000);
    }
    throw new Error('shipping');
  }
  const destination = selection => {
    const place = [selection?.locality, selection?.city, selection?.department, selection?.country]
      .filter((value, index, all) => value && all.indexOf(value) === index);
    if (selection?.country !== 'Colombia' && selection?.postalCode) place.push(selection.postalCode);
    return place.join(', ');
  };
  const confirmedService = (selection, options) => {
    const saved = selection?.whatsappShipping;
    if (selection?.country !== 'Colombia' || normalize(selection.city) !== 'bogota' || !saved || saved.locality !== selection.locality) return '';
    return options.find(option => option.id === saved.id && option.price === saved.price && option.time === saved.time)?.id || '';
  };
  const needsDestination = (cart, selection, skipEmpty = false) => Boolean(!skipEmpty
    && (cart.items?.length ? cart.requires_shipping : true)
    && (!selection?.country
      || (selection.country === 'Colombia'
        ? (!selection.city || (normalize(selection.city) === 'bogota' ? !selection.locality : !selection.department))
        : !selection.postalCode)));
  const shouldPromptOnEntry = (selection, dismissed = false, designMode = false) => !designMode && !dismissed && needsDestination({ items: [], requires_shipping: false }, selection);
  const initialDestinationExperience = (detectedCountryCode, currentCountryCode = '') => {
    const detected = String(detectedCountryCode || '').toUpperCase();
    const current = String(currentCountryCode || '').toUpperCase();
    return detected === 'CO' || (!detected && current === 'CO') ? 'compact-colombia' : 'selector';
  };
  const nationalEnquiry = (selection, options) => selection?.country === 'Colombia' && Boolean(selection.city && selection.department) && normalize(selection.city) !== 'bogota' && options.length === 0;
  function internationalOptions(options, selection, labels, international = false) {
    if (!international) return options;
    const time = normalize(selection?.city) === 'bogota' ? labels.international_bogota : labels.international_national;
    return options
      .filter(option => option.id !== 'express' && !normalize(option.label).includes('express'))
      .map(option => ({ ...option, time }));
  }
  function shippingOptions(selector, rates, selection, cart, labels, international = false) {
    if (!selector || !rates || cart.currency !== 'COP' || selection?.country !== 'Colombia' || !selection.city || (cart.items?.length && !cart.requires_shipping)) return [];
    if (selector.normalize(selection.city) === 'bogota') {
      const options = [];
      const economy = rates.bogota?.economy;
      if (economy?.time && Number.isFinite(economy.price) && economy.price >= 0) options.push({ id: 'economy', label: labels.economy, price: economy.price * 100, time: selector.economyPromise().promise });
      const express = selection.locality && rates.bogota?.express?.[selector.normalize(selection.locality)];
      if (express?.time && /\d+\s*(?:a|y)\s*\d+\s*horas?/i.test(express.time) && Number.isFinite(express.price) && express.price >= 0) options.push({ id: 'express', label: labels.express, price: express.price * 100, time: selector.expressPromise(express.time).promise });
      return internationalOptions(options, selection, labels, international);
    }
    return [];
  }
  function message(cart, selection, options, chosen, labels, international = false) {
    const money = value => new Intl.NumberFormat('es-CO', { style: 'currency', currency: cart.currency || 'COP', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value / 100);
    const lines = [cart.items?.length ? labels.greeting_cart : labels.greeting];
    for (const item of cart.items || []) {
      lines.push(`• ${item.quantity} × ${item.title} — ${money(item.final_price)} ${labels.unit}; ${labels.line_total}: ${money(item.final_line_price)}`);
    }
    if (cart.items?.length) lines.push('', `${labels.subtotal}: ${money(cart.total_price)}`);
    lines.push('', `📍 ${labels.destination}: ${destination(selection) || labels.pending}`);
    if (['Estados Unidos', 'Canadá'].includes(selection?.country)) {
      lines.push(international ? labels.us_ca_international_delivery : labels.us_ca_delivery);
    }
    if (cart.items?.length && international && selection?.country === 'Colombia') lines.push(labels.international_notice);
    if (!cart.items?.length) {
      const selected = options.find(option => option.id === chosen);
      if (selected) lines.push(selected.label);
    }
    if (cart.items?.length) {
      const selected = options.find(option => option.id === chosen);
      if (!cart.requires_shipping) lines.push(`${labels.estimated_total}: ${money(cart.total_price)}`);
      else if (!options.length) lines.push(labels.shipping_pending);
      else if (!selected) lines.push(labels.unselected);
      else lines.push(`${selected.label}: ${money(selected.price)}${selected.time ? ` · ${selected.time}` : ''}`, `${labels.estimated_total}: ${money(cart.total_price + selected.price)}`);
      lines.push('', labels.disclaimer);
    }
    return lines.join('\n');
  }
  root.ElPortalWhatsAppCart = { nationalEnquiry, confirmedService, destination, needsDestination, shouldPromptOnEntry, initialDestinationExperience, internationalOptions, shippingOptions, nationalOptions, quoteNational, cartKey, message };
  if (!root.document) return;
  const dialog = document.querySelector('[data-epv-wa-dialog]');
  if (!dialog) return;
  const labels = JSON.parse(dialog.querySelector('[data-epv-wa-labels]').textContent);
  const content = dialog.querySelector('[data-epv-wa-content]');
  const status = dialog.querySelector('[data-epv-wa-status]');
  const choices = dialog.querySelector('[data-epv-wa-choices]');
  const serviceSummary = dialog.querySelector('[data-epv-wa-service-summary]');
  const serviceLabel = dialog.querySelector('[data-epv-wa-service-label]');
  const changeService = dialog.querySelector('[data-epv-wa-change-service]');
  const preview = dialog.querySelector('textarea');
  const send = dialog.querySelector('[data-epv-wa-send]');
  const destinationPrompt = dialog.querySelector('[data-epv-wa-destination-prompt]');
  const destinationHelp = dialog.querySelector('[data-epv-wa-destination-help]');
  const whatsappHelp = destinationHelp.textContent;
  const skipDestination = dialog.querySelector('[data-epv-wa-skip-destination]');
  const destinationSummary = dialog.querySelector('[data-epv-wa-destination-summary]');
  const heading = dialog.querySelector('#epv-wa-title');
  const originalHeading = heading.textContent;
  const whatsappLink = document.querySelector('[data-elportal-whatsapp-button]');
  let choosingDestination = false;
  let entryMode = 'whatsapp';
  let skipEmptyDestination = false;
  let request = 0;
  let chosen = '';
  let lastDestination = '';
  let trigger;
  let timer;
  let controller;
  let previousCart = '';
  let destinationSearchContext = null;
  function preservePredictiveSearch(source) {
    const predictiveSearch = source?.closest?.('details-modal.header__search predictive-search');
    const details = predictiveSearch?.closest?.('details-modal.header__search')?.querySelector('details[open]');
    const query = predictiveSearch?.getQuery?.() || '';
    if (!predictiveSearch || !details || !query || predictiveSearch.getAttribute('results') !== 'true') return null;
    document.body.classList.add('elportal-quick-add-search-active');
    const results = predictiveSearch.querySelector('[data-predictive-search]');
    return { predictiveSearch, query, scrollTop: results?.scrollTop || 0 };
  }
  function restorePredictiveSearch() {
    const context = destinationSearchContext;
    destinationSearchContext = null;
    if (!context?.predictiveSearch?.isConnected) return;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const predictiveSearch = context.predictiveSearch;
      const details = predictiveSearch.closest('details-modal.header__search')?.querySelector('details[open]');
      if (!details) return;
      document.body.classList.add('elportal-quick-add-search-active');
      predictiveSearch.openPredictiveSearchShell?.();
      const results = predictiveSearch.querySelector('[data-predictive-search]');
      if (predictiveSearch.getQuery?.() === context.query
        && predictiveSearch.getAttribute('results') === 'true'
        && results?.innerHTML.trim()) {
        predictiveSearch.open?.();
      } else {
        predictiveSearch.restoreSharedSearchUrl?.(context.query);
      }
      if (results) {
        results.scrollTop = context.scrollTop;
        try {
          results.focus({ preventScroll: true });
        } catch (_error) {
          results.focus();
        }
      }
    }));
  }
  const enableSend = text => {
    if (!whatsappLink?.href) return;
    const url = new URL(whatsappLink.href);
    if (text) url.searchParams.set('text', text);
    send.href = url.href;
    send.setAttribute('aria-disabled', 'false');
  };
  const invalidate = () => {
    request += 1;
    controller?.abort();
    send.removeAttribute('href');
    send.setAttribute('aria-disabled', 'true');
    enableSend(labels.greeting);
    content.hidden = true;
  };
  async function refresh() {
    if (entryMode !== 'whatsapp') return;
    invalidate();
    controller = new AbortController();
    const activeController = controller;
    const signal = controller.signal;
    const timeout = setTimeout(() => activeController.abort(), 20000);
    const id = request;
    status.textContent = labels.loading;
    destinationPrompt.hidden = true;
    heading.textContent = originalHeading;
    try {
      const prefix = root.Shopify?.routes?.root || '/';
      const response = await fetch(`${prefix}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal });
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) throw new Error('cart');
      const selector = document.querySelector('epv-delivery-selector');
      const selection = selector?.readSelection?.();
      if (id !== request || !dialog.open) return;
      destinationSummary.hidden = !destination(selection);
      dialog.querySelector('[data-epv-wa-destination-label]').textContent = destination(selection);
      skipDestination.hidden = false;
      if (needsDestination(cart, selection, skipEmptyDestination)) {
        heading.textContent = labels.ask_destination;
        destinationPrompt.hidden = false;
        destinationSummary.hidden = true;
        status.textContent = '';
        chosen = '';
        return;
      }
      const key = JSON.stringify(selection);
      const importService = key !== lastDestination || cartKey(cart) !== previousCart;
      if (key !== lastDestination) chosen = '';
      lastDestination = key;
      if (cartKey(cart) !== previousCart) chosen = '';
      previousCart = cartKey(cart);
      const isBogota = normalize(selection?.city) === 'bogota' && selection?.country === 'Colombia';
      const rates = isBogota ? await selector?.loadShippingRates?.() : null;
      if (id !== request || !dialog.open) return;
      const international = cart.items.some(item => root.ElPortalInternationalPurchase?.isProductUrl?.(item.url));
      let options = shippingOptions(selector, rates, selection, cart, labels, international);
      const needsShipping = cart.items.length > 0 && cart.requires_shipping;
      if (needsShipping && !isBogota && selection?.country === 'Colombia' && selection.city && selection.department) {
        try {
          options = await quoteNational(selection, cart.currency, selector, fetch, prefix, signal);
          options = internationalOptions(options, selection, labels, international);
        } catch (error) {
          if (signal.aborted) throw error;
          options = [];
        }
        const check = await fetch(`${prefix}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal });
        if (!check.ok || cartKey(await check.json()) !== cartKey(cart)) throw new Error('cart-changed');
      }
      if (id !== request || !dialog.open) return;
      if (!options.some(option => option.id === chosen)) chosen = '';
      if (importService) chosen = confirmedService(selection, options);
      if (options.length === 1 && needsShipping) chosen = options[0].id;
      const update = () => {
        preview.value = message(cart, selection, options, chosen, labels, international);
        const enquiry = needsShipping && options.length === 0;
        status.textContent = enquiry ? labels.quote_enquiry : '';
        const selected = options.find(option => option.id === chosen);
        choices.hidden = options.length < 2 || Boolean(selected);
        serviceSummary.hidden = !selected;
        changeService.hidden = options.length < 2;
        if (selected) {
          const money = value => new Intl.NumberFormat('es-CO', {style:'currency',currency:cart.currency,minimumFractionDigits:0,maximumFractionDigits:2}).format(value / 100);
          serviceLabel.textContent = cart.items.length
            ? `${selected.label} · ${money(selected.price)} · ${selected.time} — ${labels.estimated_total}: ${money(cart.total_price + selected.price)}`
            : selected.label;
        }
        enableSend(preview.value);
      };
      choices.replaceChildren();
      changeService.onclick = () => { choices.hidden = false; serviceSummary.hidden = true; choices.querySelector('input:checked, input')?.focus(); };
      choices.hidden = options.length < 2;
      if (options.length > 1) {
        const legend = document.createElement('legend');
        legend.textContent = labels.choose;
        choices.append(legend);
        for (const option of options) {
          const label = document.createElement('label');
          const input = document.createElement('input');
          input.type = 'radio'; input.name = 'epv-wa-service'; input.value = option.id; input.checked = option.id === chosen;
          input.addEventListener('change', () => { chosen = option.id; update(); });
          const text = document.createElement('span');
          const price = new Intl.NumberFormat('es-CO', {style:'currency',currency:cart.currency,minimumFractionDigits:0,maximumFractionDigits:2}).format(option.price / 100);
          text.textContent = `${option.label} · ${price}${option.time ? ` · ${option.time}` : ''}`;
          label.append(input, text); choices.append(label);
        }
      }
      update();
      content.hidden = false;
    } catch (error) {
      if (id === request && dialog.open) {
        const selection = document.querySelector('epv-delivery-selector')?.readSelection?.();
        destinationPrompt.hidden = true;
        destinationSummary.hidden = !destination(selection);
        dialog.querySelector('[data-epv-wa-destination-label]').textContent = destination(selection);
        choices.hidden = true;
        serviceSummary.hidden = true;
        preview.value = message({ currency: 'COP', items: [], requires_shipping: false, total_price: 0 }, selection, [], '', labels);
        enableSend(preview.value);
        content.hidden = false;
        status.textContent = error.message === 'shipping' ? labels.no_quote : labels.error;
      }
    } finally { clearTimeout(timeout); }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-elportal-whatsapp-button], [data-elportal-mobile-whatsapp], [data-elportal-desktop-whatsapp]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    entryMode = 'whatsapp';
    destinationHelp.textContent = whatsappHelp;
    trigger = link;
    skipEmptyDestination = false;
    if (!dialog.open) dialog.showModal();
    refresh();
    clearInterval(timer);
    timer = setInterval(() => { if (dialog.open) refresh(); }, 60000);
  });
  dialog.querySelector('[data-epv-wa-close]').addEventListener('click', () => dialog.close());
  function openDestinationPrompt(source = trigger) {
    entryMode = 'destination';
    trigger = source;
    destinationSearchContext = preservePredictiveSearch(source) || destinationSearchContext;
    invalidate();
    clearInterval(timer);
    heading.textContent = labels.ask_destination;
    destinationHelp.textContent = labels.destination_only_help;
    destinationPrompt.hidden = false;
    destinationSummary.hidden = true;
    skipDestination.hidden = false;
    status.textContent = '';
    if (!dialog.open) dialog.showModal();
    return true;
  }
  root.ElPortalWhatsAppCart.openDestinationPrompt = openDestinationPrompt;
  function chooseDestination(useLocation = false) {
    skipEmptyDestination = false;
    const selector = document.querySelector('epv-delivery-selector');
    if (!selector?.open || !selector.dialog) { status.textContent = labels.error; return; }
    choosingDestination = true;
    selector.dialog.addEventListener('close', () => {
      choosingDestination = false;
      if (entryMode === 'destination') {
        if (selector.selectionSaved) {
          trigger?.focus();
          restorePredictiveSearch();
        }
        else if (!selector.wasDismissedThisSession?.()) openDestinationPrompt();
        else {
          trigger?.focus();
          restorePredictiveSearch();
        }
        return;
      }
      if (selector.selectionSaved) lastDestination = '';
      if (!dialog.open) dialog.showModal();
      refresh();
      clearInterval(timer);
      timer = setInterval(() => { if (dialog.open) refresh(); }, 60000);
    }, { once: true });
    dialog.close();
    if (entryMode === 'destination') {
      selector.open({ screen: 'colombia', allowBrowse: false, focusSearch: !useLocation });
      if (useLocation) selector.requestDeviceLocation?.();
      return;
    }
    selector.open({ suggestDetected: true, allowBrowse: false });
  }
  dialog.querySelector('[data-epv-wa-destination]').addEventListener('click', () => chooseDestination());
  dialog.querySelector('[data-epv-wa-search]').addEventListener('click', () => chooseDestination());
  dialog.querySelector('[data-epv-wa-geolocate]').addEventListener('click', () => chooseDestination(true));
  skipDestination.addEventListener('click', () => {
    if (entryMode === 'destination') { dialog.close(); return; }
    skipEmptyDestination = true;
    refresh();
  });
  send.addEventListener('click', event => { if (!send.hasAttribute('href')) event.preventDefault(); });
  dialog.addEventListener('close', () => {
    invalidate();
    clearInterval(timer);
    if (entryMode === 'destination' && !choosingDestination) {
      const selector = document.querySelector('epv-delivery-selector');
      if (!selector?.hasCompleteSelection?.()) selector?.rememberSessionDismissal?.();
    }
    if (!choosingDestination) {
      trigger?.focus();
      if (entryMode === 'destination') restorePredictiveSearch();
    }
  });
  document.addEventListener('epv:destination-changed', () => { if (dialog.open) refresh(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden && dialog.open) refresh(); });
  function promptForDestinationOnEntry() {
    const selector = document.querySelector('epv-delivery-selector');
    if (!selector || !shouldPromptOnEntry(selector.readSelection?.(), selector.wasDismissedThisSession?.(), Boolean(root.Shopify?.designMode))) return;
    root.requestAnimationFrame(() => {
      if (dialog.open || selector.dialog?.open) return;
      const currentCountryCode = selector.currentCountryCode || '';
      const detectedCountry = ['US', 'CA'].includes(String(currentCountryCode).toUpperCase())
        ? Promise.resolve(currentCountryCode)
        : selector.fetchDetectedCountryCode?.() || Promise.resolve(null);
      detectedCountry.then(countryCode => {
        if (dialog.open || selector.dialog?.open) return;
        if (!shouldPromptOnEntry(selector.readSelection?.(), selector.wasDismissedThisSession?.(), Boolean(root.Shopify?.designMode))) return;
        trigger = document.activeElement;
        if (initialDestinationExperience(countryCode, currentCountryCode) === 'compact-colombia') {
          openDestinationPrompt(trigger);
          return;
        }
        selector.open({ suggestDetected: true });
      });
    });
  }
  promptForDestinationOnEntry();
})(typeof window === 'undefined' ? globalThis : window);
